import { useCallback, useMemo, useState } from "react";
import { AuditLogFilter, HistoryItem } from "@common/types/auditLog";
import { ChartOfAccountsTemplate } from "@common/types/chartOfAccountsTemplate";
import { removeTimeZoneOffset } from "@helpers/date";
import {
    HistoryFilters,
    RevertedFilterValue,
} from "@hooks/view/useHistoryPageFiltersCache";
import { useChartOfAccountsTemplateActivity } from "@api/coaTemplateActivity.api";
import { useWorkspacesList } from "@api/workspace.api";
import { useScope } from "@state/scopeContext.lib";
import { Drawer } from "@design-system/Drawer/Drawer";
import { Loader } from "@design-system/Loader/Loader";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { DateRangeFilterNext } from "@design-system/DateRangeFilter/DateRangeFilterNext";
import {
    toDateOnlyDateRange,
    toLocalDateRange,
} from "@design-system/DateRangeFilter/DateRangeFilterNext.lib";
import { Pagination } from "@design-system/Pagination/Pagination";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { History } from "@components/history/History";
import { HistoryDrawerContextProvider } from "@components/history/HistoryDrawerContextProvider";
import { historyEntityMapContext } from "@components/history/historyEntityMapContext";
import { buildHistoryEntityMap } from "@components/history/historyEntityMap";
import { ActivityFilterCombobox } from "@components/history/components/ActivityFilterCombobox";
import {
    buildActivityFilters,
    getActivitiesFromValues,
} from "@components/history/components/historyActivityFilters";
import { ObjectFieldFilterCombobox } from "@components/history/components/ObjectFieldFilterCombobox";
import { historyItemToRow } from "@components/history/utils/historyItemToRow";
import styles from "./ChartOfAccountsTemplateActivityDrawer.module.scss";

const PAGE_SIZE = 20;
const DEFAULT_FILTERS: HistoryFilters = {};

export const ChartOfAccountsTemplateActivityDrawer = ({
    open,
    template,
    onClose,
}: {
    open: boolean;
    template: ChartOfAccountsTemplate | null;
    onClose: () => void;
}) => (
    <Drawer
        open={open}
        onClose={onClose}
        direction="right"
        size="fullScreenWithNav"
        noPadding
        headerAddonLeft={
            <h2 className={styles.title}>
                Activity · {template?.name ?? "Chart of accounts template"}
            </h2>
        }
    >
        {/* Keying on the template id remounts the content per template so a
            prior template's filters/page never leak across drawer close or
            template switch (the Drawer shell itself stays mounted). */}
        {open && template && (
            <ChartOfAccountsTemplateActivityDrawerContent
                key={template.id}
                template={template}
            />
        )}
    </Drawer>
);

const ChartOfAccountsTemplateActivityDrawerContent = ({
    template,
}: {
    template: ChartOfAccountsTemplate;
}) => {
    const [filters, setFilters] = useState<HistoryFilters>(DEFAULT_FILTERS);
    const [page, setPage] = useState(1);
    const { organizationId } = useScope();
    const apiFilters = useMemo(() => buildApiFilters(filters), [filters]);
    const query = useChartOfAccountsTemplateActivity({
        templateId: template.id,
        query: { filters: apiFilters, page, limit: PAGE_SIZE },
        enabled: true,
    });
    // Template Activity runs at organization scope, so the workspace-scoped
    // entity lookup is empty. Fetch the organization's workspaces (with their
    // entities) once and expose an id → entity map to the shared History cells.
    const { data: workspaces = [] } = useWorkspacesList(
        {
            organizationId: organizationId ?? undefined,
            includeEntities: true,
        },
        { enabled: !!organizationId },
    );
    const entityIconsById = useMemo(
        () => buildHistoryEntityMap(workspaces),
        [workspaces],
    );
    const rows = useMemo(
        () => (query.data?.data ?? []).map(mapHistoryItemToRow),
        [query.data?.data],
    );
    const dateRange = useMemo(
        () => toDateOnlyDateRange({ start: filters.start, end: filters.end }),
        [filters.end, filters.start],
    );
    const updateFilters = useCallback((next: Partial<HistoryFilters>) => {
        setFilters((current) => ({ ...current, ...next }));
        setPage(1);
    }, []);
    const clearFilters = useCallback(() => {
        setFilters(DEFAULT_FILTERS);
        setPage(1);
    }, []);
    const renderTable = () => {
        if (query.isLoading) {
            return <Loader />;
        }
        if (query.isError) {
            return <EmptyState header="Couldn't load template activity" />;
        }
        return (
            <historyEntityMapContext.Provider value={entityIconsById}>
                <History
                    data={rows}
                    clearFilters={clearFilters}
                    footer={
                        (query.data?.totalPages ?? 1) > 1 ? (
                            <Pagination
                                currentPage={page}
                                pageCount={query.data?.totalPages ?? 1}
                                onPageChange={setPage}
                            />
                        ) : undefined
                    }
                />
            </historyEntityMapContext.Provider>
        );
    };

    return (
        <HistoryDrawerContextProvider>
            <div className={styles.content}>
                <TablePageHeader
                    filters={filters}
                    onFiltersChange={updateFilters}
                    defaultFilters={DEFAULT_FILTERS}
                    isFullPage
                    prependChildren={
                        <DateRangeFilterNext
                            dateRange={dateRange}
                            onChange={(value) => {
                                const localRange = toLocalDateRange(value);
                                updateFilters({
                                    start: localRange.start,
                                    end: localRange.end,
                                });
                            }}
                        />
                    }
                >
                    <ObjectFieldFilterCombobox
                        filters={filters}
                        onChange={updateFilters}
                    />
                    <ActivityFilterCombobox
                        filters={filters}
                        onChange={updateFilters}
                    />
                </TablePageHeader>
                <div className={styles.table}>{renderTable()}</div>
            </div>
        </HistoryDrawerContextProvider>
    );
};

function buildApiFilters(filters: HistoryFilters): AuditLogFilter {
    const result: AuditLogFilter = {};
    if (filters.activityValues?.length) {
        result.activityFilters = buildActivityFilters(
            getActivitiesFromValues(filters.activityValues),
            filters.changeTypes,
        );
    }
    if (filters.resourceTypes?.length) {
        result.resourceTypes = filters.resourceTypes;
    }
    if (filters.start) {
        result.minCreatedAt = removeTimeZoneOffset(filters.start);
    }
    if (filters.end) {
        result.maxCreatedAt = removeTimeZoneOffset(filters.end);
    }
    if (filters.revertedFilter === RevertedFilterValue.REVERTED) {
        result.revertedOnly = true;
    } else {
        result.showReverted = true;
    }
    return result;
}

function mapHistoryItemToRow(item: HistoryItem) {
    return historyItemToRow({
        item,
        id: `${item.sourceType}-${item.sourceIds.join("-")}-${item.resourceIds.join("-")}`,
    });
}
